import { marked } from "marked";
import last from "lodash/last";
import type { Token, Tokens } from "marked";
import { IKeyValueMap } from "mobx";
import { IVideoClipInfo, EVideoInfoParseStatus } from "../../video/types";

export interface IOptions {}

interface ParsedResult {
  index: number;
  start: number;
  end: number;
  title: string;
  other: string;
}

function parseTimeString(input: string): ParsedResult {
  const match = input.match(
    /(\[视频(\d)\]\s?)?\[(\d{2}):(\d{2})-(\d{2}):(\d{2})\]\s([^（]+)(?:（([^）]+)）)?/,
  );
  if (!match) {
    throw new Error("Invalid format");
  }

  const [, , video, startMin, startSec, endMin, endSec, description, other] =
    match;

  return {
    index: video && Number(video) ? Number(video) : 1,
    start: (parseInt(startMin) * 60 + parseInt(startSec)) * 1000,
    end: (parseInt(endMin) * 60 + parseInt(endSec)) * 1000,
    title: description.trim(),
    other: other ? other.trim() : "",
  };
}

class VideoParser {
  rawText: string = "";
  nodes: IVideoClipInfo[] | undefined = undefined;
  parseData: {
    status: EVideoInfoParseStatus;
    data?: IVideoClipInfo[] | undefined;
    otherStr?: string;
  } = {
    status: EVideoInfoParseStatus.parsing,
  };

  clear() {
    this.rawText = "";
    this.nodes = [];
  }

  write(v: string, end?: boolean) {
    try {
      this.rawText = this.rawText + v;
      const tokens = marked.lexer(this.rawText) as Token[];
      const lastToken: Token | undefined = last(tokens);
      if (this.rawText.length < 22 && !end) {
        this.parseData = {
          status: EVideoInfoParseStatus.parsing,
        };
        return this.parseData;
      }

      if (
        end ||
        (lastToken?.type !== "list" &&
          lastToken?.type !== "space" &&
          (lastToken?.type !== "paragraph" ||
            (lastToken?.type === "paragraph" && lastToken.raw.length > 4)))
      ) {
        const parseResult = this.parse(tokens);
        // 如果解析的每项都出错说明不是视频剪辑
        if (parseResult?.length === 0 || parseResult === undefined) {
          this.parseData = {
            status: EVideoInfoParseStatus.error,
            otherStr: this.rawText,
          };
          return this.parseData;
        } else {
          this.parseData = {
            status: EVideoInfoParseStatus.end,
            data: parseResult,
            otherStr: lastToken?.raw,
          };
          return this.parseData;
        }
      } else {
        const parseResult = this.parse(tokens);
        // 如果解析的每项都没有title说明不是视频剪辑
        if (
          parseResult &&
          parseResult?.length > 1 &&
          parseResult?.every(item => !item?.title)
        ) {
          this.parseData = {
            status: EVideoInfoParseStatus.error,
            otherStr: this.rawText,
          };
          return this.parseData;
        } else {
          this.parseData = {
            status: EVideoInfoParseStatus.parsing,
          };
          return this.parseData;
        }
      }
    } catch (e) {
      console.error("视频剪辑解析错误:", e);
      this.parseData = {
        status: EVideoInfoParseStatus.error,
        otherStr: this.rawText,
      };
      return this.parseData;
    }
  }

  parse(tokens: Token[]): IVideoClipInfo[] | undefined {
    const list: Tokens.List | undefined = tokens.find(
      it => it.type === "list",
    ) as Tokens.List;
    if (list) {
      return this.parse_list(list);
    }
  }

  parse_list(
    token: Tokens.List,
    context?: IKeyValueMap,
  ): IVideoClipInfo[] | undefined {
    if (token.ordered) {
      return token.items
        .map(item => this.parse_main_list_item(item, context))
        .filter(item => !!item) as IVideoClipInfo[];
    }
  }

  parse_main_list_item(
    token: Tokens.ListItem,
    context?: IKeyValueMap,
  ): IVideoClipInfo | undefined {
    const titleToken = token.tokens[0] as Tokens.Text;
    const listToken = token.tokens.find(
      item => item.type === "list",
    ) as Tokens.List;
    if (
      titleToken &&
      listToken &&
      listToken.items?.[1] &&
      listToken.items?.[1].tokens[0]
    ) {
      const res = parseTimeString(titleToken.text);
      const description =
        listToken.items?.[0] &&
        this.parse_sub_list_item(listToken.items[0], context);
      const reason =
        listToken.items?.[1] &&
        this.parse_sub_list_item(listToken.items[1], context);
      const commentary =
        listToken.items?.[2] &&
        listToken.items?.[2].tokens[0] &&
        this.parse_sub_list_item(listToken.items[2], context);
      return {
        ...res,
        description: description || "",
        reason: reason || "",
        commentary: commentary || "",
      };
    }
  }

  parse_sub_list_item(token: Tokens.ListItem, context?: IKeyValueMap) {
    if (token.tokens?.[0]?.type === "text") {
      const textToken = token.tokens[0] as Tokens.Text;
      if (
        textToken.tokens?.[0]?.type === "strong" &&
        textToken.tokens?.[1]?.type === "text"
      ) {
        let result: string = textToken.tokens?.[1]?.text?.trim();

        if (result && (result.startsWith(":") || result.startsWith("："))) {
          return result.slice(1).trim();
        } else {
          return result;
        }
      }
    }
  }
}

export default VideoParser;
